import { computed, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useBuilder } from './builder.store';
import { usePrimaryResumeStore } from './primary-resume.store';

import { getResumeScore, rewriteBullet } from '../api/resume-score';

import { categoryOf, groupSuggestionsByCategory, SUGGESTION_CATEGORIES, suggestionKey } from '../utils/score-suggestions';
import { findBullet, hasBullet, replaceBulletText, restoreBullet } from '../utils/bullet-html';
import { RESOLVED_DISPLAY_MS } from '../composables/useResolvingSuggestions';
import { useOnboardingChecklist } from './onboarding-checklist.store';

// EN-936: recommendations used to be dismissible into this key, which hid the
// only instruction for recovering a category's missing points while the
// server-computed score stayed put — permanently, with no undo. Nothing reads
// it any more; clear the dead entry. Safe to drop a release after this ships.
localStorage.removeItem('ignored-warnings');

// EN-1195: how long to wait before asking again after the server fails to score, by attempt.
export const SCORE_RETRY_DELAYS_MS = [3000, 6000, 12000, 24000];

/**
 * Thin fetch/cache layer over GET /api/resumes/{resume}/score (EN-797) —
 * the server is now the single source of truth for scoring. Refetches on
 * initial load and after the resume is saved (the score endpoint's own
 * cache is keyed on the resume's last_updated_on, so there's no value in
 * refetching more often than that changes).
 */
export const useResumeScorerStore = defineStore('resume-scorer', () => {
	const route = useRoute();
	const builderStore = useBuilder();
	const primaryResumeStore = usePrimaryResumeStore();

	const { primaryResume } = storeToRefs(primaryResumeStore);
	const { data, saveCount } = storeToRefs(builderStore);

	const showResumeScorer = ref(false);
	const showBreakdownModal = ref(false);

	const breakdown = ref({});
	const total = ref(0);
	const maxTotal = ref(100);
	const alignmentStatus = ref(null);
	// EN-796: 'v2' once the resume has a target title/level/track selection
	// (or an explicit v2 query-param request); null for the plain v1 total,
	// which always defaults alignmentStatus to 'locked' even with no target
	// selected — scoreVersion is the reliable "has a target been set" signal.
	const scoreVersion = ref(null);
	// EN-799: the currently-selected (specialization, level, track) the score
	// was computed against — carried on the response so the needs_confirmation
	// "switch specialization" flow can resubmit the same level/track via
	// setTargetSelection() without the frontend needing to track it separately.
	const targetSelection = ref(null);
	// EN-798: ranked (desc. point-lift) v2 suggestions, e.g. { type, label,
	// message, pointLift, anchor }. Empty for the v1 total, which never sets this.
	const suggestions = ref([]);
	// EN-1039: the resume `suggestions` were scored for. The builder can still hold the previous
	// resume's score while the next one loads, so a deep link checks this before acting.
	const scoredResumeUuid = ref(null);
	// EN-812: the server found the resume doesn't have enough real content to score yet. Only a
	// score response sets it, so a failed fetch never shows the pending guidance.
	const pending = ref(false);
	// EN-812: whether Content Strength has any real bullet to suggest fixes for.
	const hasRealBullets = ref(true);
	// EN-812: per category, the fixes detected but not revealed to this account.
	const noneLocked = () => Object.fromEntries(SUGGESTION_CATEGORIES.map((category) => [category, 0]));
	const lockedCounts = ref(noneLocked());
	const isLoading = ref(false);
	// EN-1003: the builder refetches after every save, so requests can overlap —
	// an older save's score must never replace a newer one's.
	let latestRequest = 0;
	// EN-1195: the request whose score is on screen. Rescoring a changed resume takes seconds, and
	// every save, card remount and panel open asks again, so waiting for only the newest request
	// kept the old score up for as long as the user kept editing. A score of a save at least as new
	// as the one on screen is shown as soon as it lands; of two for the same save, the later asked.
	let appliedRequest = 0;
	let retryTimer = null;
	// EN-1146: rewrites the user hasn't accepted or undone yet, one per bullet. Each keeps the card
	// as it was when they clicked Rewrite, and the bullet's markup from before, for Undo.
	const rewrites = ref([]);
	// EN-1146: the builder's save count when the score on screen was fetched, so Accept can tell
	// whether that score has seen its rewrite yet.
	let scoredAt = -1;

	// EN-1195: a failed rescore left the old score up until a reload. A server error, including
	// kamal-proxy giving up while the server goes on to cache the score, is asked again.
	const retryAfterServerError = (error, request, attempt) => {
		if (request === latestRequest && error?.response?.status >= 500 && attempt < SCORE_RETRY_DELAYS_MS.length) {
			retryTimer = setTimeout(() => fetchScore(attempt + 1), SCORE_RETRY_DELAYS_MS[attempt]);
		}
	};

	const fetchScore = async (attempt = 0) => {
		clearTimeout(retryTimer);
		const resumeUuid = data.value?.uuid;

		if (resumeUuid == null) {
			return;
		}

		latestRequest += 1;
		const request = latestRequest;
		const savedAt = saveCount.value;
		isLoading.value = true;

		try {
			// EN-812: only a score the panel shows may spend the account's reveals.
			const response = await getResumeScore(resumeUuid, { reveal: showResumeScorer.value });
			const isNewerScore = savedAt > scoredAt || (savedAt === scoredAt && request > appliedRequest);

			if (resumeUuid !== data.value?.uuid || !isNewerScore) {
				return;
			}

			appliedRequest = request;
			breakdown.value = response.breakdown || {};
			total.value = response.total || 0;
			maxTotal.value = response.max_total || 100;
			alignmentStatus.value = response.alignment_status || null;
			scoreVersion.value = response.score_version || null;
			targetSelection.value = response.target_selection || null;
			suggestions.value = response.suggestions || [];
			pending.value = response.pending ?? false;
			hasRealBullets.value = response.has_real_bullets ?? true;
			lockedCounts.value = { ...noneLocked(), ...response.locked_counts };
			scoredResumeUuid.value = resumeUuid;
			scoredAt = savedAt;
			// EN-1146: an undone rewrite's card goes once a score of the restored bullet is in, and an
			// accepted one is settled by the first score of the rewritten bullet.
			rewrites.value = rewrites.value.filter((rewrite) => rewrite.status !== 'undoing' || rewrite.undoneAt >= savedAt);
			rewrites.value
				.filter((rewrite) => rewrite.status === 'accepting' && rewrite.writtenAt < savedAt)
				.forEach((rewrite) => settleAccepted(rewrite.key));
		} catch (error) {
			retryAfterServerError(error, request, attempt);

			// Keep the last known score on the screen rather than blanking it out, unless it is
			// another resume's: then show this resume as scored with nothing known, so the sidebar
			// still offers the role form (EN-814) rather than the previous resume's role.
			if (request === latestRequest && scoredResumeUuid.value !== resumeUuid) {
				breakdown.value = {};
				total.value = 0;
				maxTotal.value = 100;
				alignmentStatus.value = null;
				scoreVersion.value = null;
				targetSelection.value = null;
				suggestions.value = [];
				pending.value = false;
				hasRealBullets.value = true;
				lockedCounts.value = noneLocked();
				scoredResumeUuid.value = resumeUuid;
			}
		} finally {
			if (request === latestRequest) {
				isLoading.value = false;
			}
		}
	};

	const setShowResumeScorer = () => {
		showResumeScorer.value = true;
	};

	const hideResumeScorer = () => {
		showResumeScorer.value = false;
	};

	const setShowBreakdown = () => {
		showBreakdownModal.value = true;
	};

	const hideBreakdown = () => {
		showBreakdownModal.value = false;
	};

	watch(route, () => {
		showResumeScorer.value = false;
	});

	// EN-812: the score on screen was fetched with the panel closed, so it spent nothing. Opening
	// the panel fetches again to reveal what it may.
	watch(showResumeScorer, (open) => {
		if (open) {
			fetchScore();
		}
	});

	// EN-812: the ring shows "—" rather than 0 while the resume is pending, and while the first
	// score is still on its way.
	const noScoreYet = computed(() => pending.value || (isLoading.value && scoredResumeUuid.value === null));

	// Sums a pillar's named leaf scores. Kept as per-category totals (rather
	// than trusting the server total alone) because the sidebar/breakdown-
	// modal progress bars render per-category values, not just the overall
	// total.
	const pillarScore = (pillar, leafKeys) => leafKeys.reduce((sum, key) => sum + (pillar?.[key]?.score || 0), 0);

	const getScore = computed(() => {
		// EN-812: a pending resume has no score, so every pillar reads 0 wherever it is shown.
		if (pending.value) {
			return { contentScore: 0, structureScore: 0, completenessScore: 0, alignmentScore: 0, totalScore: 0 };
		}

		const raw = {
			contentScore: pillarScore(breakdown.value?.content, ['length', 'statistics', 'actionVerbs']),
			structureScore: pillarScore(breakdown.value?.structure, [
				'readAbility', 'margins', 'experienceOrder', 'sectionFormat',
			]),
			completenessScore: pillarScore(breakdown.value?.completeness, [
				'personalInformation', 'hasUrl', 'datesForExperiences', 'bulletPoints', 'educationInfo', 'experienceInfo',
			]),
			alignmentScore: breakdown.value?.alignment?.totalScore || 0,
		};

		// EN-1068: the server total rounded to the nearest point — the same
		// number the homepage card shows (DashboardStatsResource rounds the
		// same stored total). Ceiling each pillar and adding them up used to
		// read up to 3 points higher than the homepage.
		const totalScore = Math.round(total.value) || 0;

		// Largest-remainder rounding: floor every pillar, then give the points
		// still needed to reach totalScore to the pillars with the largest
		// fractional remainders. The four pillars shown always add up to the
		// total shown, and each stays within a point of its raw value.
		const rounded = Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, Math.floor(value)]));
		const pointsLeft = totalScore - Object.values(rounded).reduce((sum, value) => sum + value, 0);

		Object.keys(raw)
			.sort((a, b) => (raw[b] - rounded[b]) - (raw[a] - rounded[a]))
			.slice(0, Math.max(pointsLeft, 0))
			.forEach((key) => {
				rounded[key] += 1;
			});

		return {
			...rounded,
			totalScore,
		};
	});

	watch(
		() => getScore.value?.totalScore,
		(newScore) => {
			if (newScore == null || primaryResume?.value?.uuid !== route?.params?.id) {return;}

			try {
				if (newScore >= 75) {
					useOnboardingChecklist().markItemAsCompleted(1, 'get75ResumeScore');
				}
			} catch (error) {
				console.error('Error updating job_search_blockers:', error);
			}
		},
		{ immediate: true }
	);

	// EN-799: the suggested specialization + plausible alternatives while
	// alignment_status is needs_confirmation (empty/null otherwise).
	const getAlignment = computed(() => breakdown.value?.alignment || null);

	// EN-971: the server writes every card (label + message); the sidebar only
	// groups them by category, keeping the server's point-lift ranking.
	const getSuggestionsByCategory = computed(() => groupSuggestionsByCategory(suggestions.value));

	const rewriteFor = (card) => rewrites.value.find((rewrite) => rewrite.key === suggestionKey(card)) ?? null;

	const updateRewrite = (key, changes) => {
		rewrites.value = rewrites.value.map((rewrite) => (rewrite.key === key ? { ...rewrite, ...changes } : rewrite));
	};

	const dropRewrite = (key) => {
		rewrites.value = rewrites.value.filter((rewrite) => rewrite.key !== key);
	};

	// Puts new markup into one bullet through the builder, which saves it. False when the bullet is gone.
	const writeBullet = (anchor, edit) => {
		const bullet = findBullet(data.value?.sections, anchor);
		const html = bullet === null ? null : edit(bullet.html);

		if (html === null) {
			return false;
		}

		builderStore.changeExperience(bullet.itemUuid, html, bullet.sectionId);

		return true;
	};

	// EN-1146: rewrites the bullet `card` points at and puts the rewrite in the resume, holding the
	// card for Accept or Undo. A failed request lets the card go and rejects, so the sidebar can
	// answer a 429 with the paywall.
	const rewriteSuggestion = async (card) => {
		const key = suggestionKey(card);
		const scored = suggestions.value.find((suggestion) => suggestionKey(suggestion) === key);
		const bullet = findBullet(data.value?.sections, card.anchor);

		if (scored === undefined || bullet === null || rewrites.value.some((rewrite) => rewrite.anchor === card.anchor)) {
			return;
		}

		const resumeUuid = data.value.uuid;
		const category = categoryOf(scored);

		rewrites.value = [...rewrites.value, {
			key,
			anchor: card.anchor,
			resumeUuid,
			category,
			index: getSuggestionsByCategory.value[category].findIndex((suggestion) => suggestionKey(suggestion) === key),
			card: scored,
			original: bullet.innerHtml,
			status: 'rewriting',
			writtenAt: null,
			undoneAt: null,
		}];

		try {
			const { rewrite } = await rewriteBullet(resumeUuid, { type: scored.type, anchor: card.anchor, text: bullet.text });

			// The user may have opened another resume meanwhile, possibly a copy with the same bullet ids,
			// or left and come back, which let the card go. Either way the rewrite has nowhere to land.
			if (data.value?.uuid !== resumeUuid || !rewrites.value.some((held) => held.key === key)) {
				return;
			}

			if (writeBullet(card.anchor, (html) => replaceBulletText(html, card.anchor, rewrite))) {
				updateRewrite(key, { status: 'review', writtenAt: saveCount.value });
			} else {
				dropRewrite(key);
			}
		} catch (error) {
			dropRewrite(key);
			throw error;
		}
	};

	// EN-1146: Accept lets the card go. If the score no longer flags the bullet, it leaves through
	// its success state. If it still does (a placeholder left to fill in), the score's card stays:
	// Accept never hides a fix the score still asks for (EN-936). Only a score of the rewritten
	// bullet can tell which; until one is in, the card shows its success state and waits.
	const acceptRewrite = (card) => {
		const rewrite = rewriteFor(card);

		if (rewrite?.status !== 'review') {
			return;
		}

		if (scoredAt > rewrite.writtenAt) {
			settleAccepted(rewrite.key);
		} else {
			updateRewrite(rewrite.key, { status: 'accepting' });
		}
	};

	const settleAccepted = (key) => {
		if (suggestions.value.some((suggestion) => suggestionKey(suggestion) === key)) {
			dropRewrite(key);

			return;
		}

		updateRewrite(key, { status: 'accepted' });
		setTimeout(() => dropRewrite(key), RESOLVED_DISPLAY_MS);
	};

	// EN-1146: Undo puts the bullet back exactly as it was. The card stays until a score of the
	// restored bullet is in, so it doesn't leave and come back in between.
	const undoRewrite = (card) => {
		const rewrite = rewriteFor(card);

		if (rewrite?.status !== 'review') {
			return;
		}

		if (writeBullet(rewrite.anchor, (html) => restoreBullet(html, rewrite.anchor, rewrite.original))) {
			updateRewrite(rewrite.key, { status: 'undoing', undoneAt: saveCount.value });
		} else {
			dropRewrite(rewrite.key);
		}
	};

	// EN-1146: a category's displayed cards with each unsettled rewrite held in its place, as the
	// card was when the user clicked Rewrite. A rescore may drop the card or change its message, and
	// neither may take it away before Accept or Undo. Applied after useResolvingSuggestions, so its
	// record of what each anchor said at the last score stays untouched. Every other card says
	// whether it can be rewritten now: one rewrite per bullet, of a bullet the builder can find.
	const withRewrites = (cards, category) => {
		const held = rewrites.value
			.filter((rewrite) => rewrite.category === category && rewrite.resumeUuid === scoredResumeUuid.value)
			.sort((a, b) => a.index - b.index);
		const heldKeys = new Set(held.map((rewrite) => rewrite.key));
		const busyAnchors = new Set(rewrites.value.map((rewrite) => rewrite.anchor));
		const shown = cards
			.filter((card) => !heldKeys.has(suggestionKey(card)))
			.map((card) => ({
				...card,
				canRewrite: card.rewritable === true && !card.resolved && !busyAnchors.has(card.anchor)
					&& hasBullet(data.value?.sections, card.anchor),
			}));

		held.forEach((rewrite) => {
			shown.splice(Math.min(rewrite.index, shown.length), 0, {
				...rewrite.card,
				resolved: rewrite.status === 'accepting' || rewrite.status === 'accepted',
				canRewrite: false,
				rewriteStatus: rewrite.status,
			});
		});

		return shown;
	};

	// EN-1146: a rewrite belongs to the resume it was made on.
	watch(() => data.value?.uuid, () => {
		rewrites.value = [];
	});

	return {
		breakdown,
		total,
		maxTotal,
		alignmentStatus,
		scoreVersion,
		targetSelection,
		suggestions,
		scoredResumeUuid,
		pending,
		noScoreYet,
		hasRealBullets,
		lockedCounts,
		getAlignment,
		getSuggestionsByCategory,
		isLoading,
		getScore,

		fetchScore,
		withRewrites,
		rewriteSuggestion,
		acceptRewrite,
		undoRewrite,

		showResumeScorer,
		setShowResumeScorer,
		hideResumeScorer,

		showBreakdownModal,
		setShowBreakdown,
		hideBreakdown,
	};
});
